import {
  getCartQuery,
  getProductSellingPlansQuery,
  getProductsAfterCursorQuery,
  getProductsQuery,
  getCustomerOrdersQuery,
  getCustomerOrderQuery,
} from '@/clients/shopify/queries/'
import {
  addToCartMutation,
  cartAttributesUpdateMutation,
  createCartMutation,
  editCartItemsMutation,
  removeFromCartMutation,
} from '@/clients/shopify/queries/mutations/cart'
import moduleEnv from '@/constants/env'
import type {
  Cart,
  CartLineInput,
  Connection,
  CustomerToken,
  ProductSellingPlans,
  SellingPlanGroup,
  SellingPlanVariant,
  ShopifyAddToCartOperation,
  ShopifyCart,
  ShopifyCartOperation,
  ShopifyCreateCartOperation,
  ShopifyError,
  ShopifyGetCustomerResponseType,
  ShopifyProductSellingPlansOperation,
  ShopifyProductsOperation,
  ShopifyRemoveFromCartOperation,
  ShopifyUpdateCartAttributesOperation,
  ShopifyUpdateCartOperation,
  ShopifyCustomerOrdersOperation,
  ShopifyCustomerOrderOperation,
} from '@/types/shopify'
import { findError } from '@/utilities/error'
import { isObject } from '@/utilities/object'
import {
  createCustomerMutation,
  forgotPasswordMutation,
  getCustomerQuery,
  loginCustomerMutation,
  logoutCustomerMutation,
  resetPasswordMutation,
  updateCustomerMutation,
  customerActivateByUrlMutation,
} from './queries/mutations/customer'
import Cookie from 'js-cookie'
import { USER_ACCESS_TOKEN } from '@/constants/shopify'

const env = moduleEnv || process.env

const endpoint = `https://${env.PUBLIC_SHOPIFY_STORE_NAME}.myshopify.com/api/${env.PUBLIC_SHOPIFY_API_VERSION}/graphql.json`

export const getApiKey = () => {
  const key = env.PUBLIC_SHOPIFY_STOREFRONT_TOKEN

  return key
}

const isShopifyError = (error: unknown): error is ShopifyError => {
  if (!isObject(error)) return false

  return findError(error)
}

export async function commerceFetch<T>({
  headers,
  query,
  variables,
}: {
  headers?: HeadersInit
  query: string
  variables?: Record<string, unknown>
}): Promise<{ status: number; body: T } | never> {
  const key = getApiKey()

  try {
    const result = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': key,
        ...headers,
      },
      body: JSON.stringify({
        ...(query && { query }),
        ...(variables && { variables }),
      }),
    })

    const body = await result.json()

    if (body.errors) {
      return Promise.reject(JSON.stringify(body.errors))
    }

    return {
      status: result.status,
      body,
    }
  } catch (err) {
    if (isShopifyError(err) && err instanceof Error) {
      console.log(err)
      throw new Error(err.message)
    } else if (typeof err === 'string') {
      throw new Error(err)
    }

    return Promise.reject(err)
  }
}

const removeEdgesAndNodes = (connection: Connection<any>) => {
  if (!connection?.edges?.length) {
    return []
  }

  return connection.edges.map((edge) => edge?.node)
}

export const reshapeCart = (cart: ShopifyCart): Cart => ({
  ...cart,
  lines: removeEdgesAndNodes(cart.lines),
})

export async function createCart(): Promise<Cart> {
  const res = await commerceFetch<ShopifyCreateCartOperation>({
    query: createCartMutation,
  })

  const { cart } = res.body.data.cartCreate

  return reshapeCart(cart)
}

export async function addToCart(
  cartId: string,
  lines: {
    merchandiseId: string
    quantity: number
    sellingPlanId?: string
    attributes?: { key: string; value: string }[]
  }[],
): Promise<Cart> {
  const res = await commerceFetch<ShopifyAddToCartOperation>({
    query: addToCartMutation,
    variables: {
      cartId,
      lines,
    },
  })

  const { cart } = res.body.data.cartLinesAdd

  return reshapeCart(cart)
}

export async function removeFromCart(
  cartId: string,
  lineIds: string[],
): Promise<Cart> {
  const res = await commerceFetch<ShopifyRemoveFromCartOperation>({
    query: removeFromCartMutation,
    variables: {
      cartId,
      lineIds,
    },
  })

  const { cart } = res.body.data.cartLinesRemove

  return reshapeCart(cart)
}

export async function updateCart(
  cartId: string,
  lines: CartLineInput[],
): Promise<Cart> {
  const res = await commerceFetch<ShopifyUpdateCartOperation>({
    query: editCartItemsMutation,
    variables: {
      cartId,
      lines,
    },
  })

  const { cart } = res.body.data.cartLinesUpdate

  return reshapeCart(cart)
}

export async function updateCartAttributesReconcile(
  cartId: string,
  attributes: { key: string; value: string }[],
) {
  const res = await commerceFetch<ShopifyUpdateCartAttributesOperation>({
    query: cartAttributesUpdateMutation,
    variables: { attributes, cartId },
  })

  const { cart } = res.body.data.cartAttributesUpdate

  return reshapeCart(cart)
}

export async function getCart(cartId: string): Promise<Cart | null> {
  const res = await commerceFetch<ShopifyCartOperation>({
    query: getCartQuery,
    variables: { cartId },
  })

  if (!res.body?.data?.cart) {
    return null
  }

  const { cart } = res.body.data

  return reshapeCart(cart)
}

export async function getProductSellingPlans(
  productId: string,
): Promise<ProductSellingPlans | null> {
  const res = await commerceFetch<ShopifyProductSellingPlansOperation>({
    query: getProductSellingPlansQuery,
    variables: {
      id: productId,
    },
  })

  if (!res.body?.data?.product) {
    return null
  }

  const { variants, sellingPlanGroups } = res.body.data.product

  let reshapedVariants: SellingPlanVariant[] = removeEdgesAndNodes(
    variants,
  ).map((variant) => {
    let _variant = variant
    let sellingPlanAllocations = variant?.sellingPlanAllocations

    if (sellingPlanAllocations) {
      sellingPlanAllocations = removeEdgesAndNodes(sellingPlanAllocations)
    } else {
      sellingPlanAllocations = []
    }

    return {
      ..._variant,
      sellingPlanAllocations,
    }
  })

  const reshapedSellingPlansGroups: SellingPlanGroup[] = removeEdgesAndNodes(
    sellingPlanGroups,
  ).map((plan) => {
    let _plan = plan
    let sellingPlans = plan?.sellingPlans

    if (sellingPlans) {
      sellingPlans = removeEdgesAndNodes(sellingPlans)
    } else {
      sellingPlans = []
    }

    return {
      ..._plan,
      sellingPlans,
    }
  })

  return {
    sellingPlanGroups: reshapedSellingPlansGroups,
    variants: reshapedVariants,
  }
}

export async function getProducts(
  afterCursor: string | undefined = undefined,
  first = 250,
) {
  const res = await commerceFetch<ShopifyProductsOperation>({
    query: afterCursor ? getProductsAfterCursorQuery : getProductsQuery,
    variables: {
      first,
      after: afterCursor,
    },
  })

  return res.body.data.products
}

export async function getCustomerOrders(
  first: number,
  afterCursor: string | undefined = undefined,
) {
  const variables = {
    customerAccessToken: Cookie.get(USER_ACCESS_TOKEN),
    first,
    ...(afterCursor && { after: afterCursor }),
  }

  const res = await commerceFetch<ShopifyCustomerOrdersOperation>({
    query: getCustomerOrdersQuery,
    variables,
  })

  return res.body.data.customer.orders
}

export async function getCustomerOrder(id: string) {
  const variables = {
    customerAccessToken: Cookie.get(USER_ACCESS_TOKEN),
    id: `id:${id}`,
  }

  const res = await commerceFetch<ShopifyCustomerOrderOperation>({
    query: getCustomerOrderQuery,
    variables,
  })

  return res.body.data.customer.orders
}

export async function loginCustomer(
  email: string,
  password: string,
): Promise<CustomerToken> {
  const res = await commerceFetch<any>({
    query: loginCustomerMutation,
    variables: {
      input: {
        email,
        password,
      },
    },
  })

  const { customerAccessToken } = res.body.data.customerAccessTokenCreate

  return customerAccessToken
}

export async function createCustomer(payload: any) {
  try {
    const res = await commerceFetch<any>({
      query: createCustomerMutation,
      variables: {
        input: payload,
      },
    })

    return res.body?.data.customerCreate
  } catch (err) {
    if (typeof err === 'string') {
      return JSON.parse(err)
    }
    return err
  }
}

export async function recoverPassword(email: string) {
  const res = await commerceFetch<any>({
    query: forgotPasswordMutation,
    variables: {
      email,
    },
  })

  const data = res.body
  return data
}

export async function activateAccount(payload: {
  activationUrl: string
  password: string
}) {
  const res = await commerceFetch<any>({
    query: customerActivateByUrlMutation,
    variables: {
      password: payload.password,
      activationUrl: payload.activationUrl,
    },
  })

  const { customer, customerAccessToken, customerUserErrors } =
    res.body.data.customerActivateByUrl

  return {
    customer,
    customerAccessToken,
    customerUserErrors,
  }
}

export async function resetPassword(
  id: string,
  payload: { token: string; password: string },
) {
  const res = await commerceFetch<any>({
    query: resetPasswordMutation,
    variables: {
      id: `gid://shopify/Customer/${id}`,
      input: {
        password: payload.password,
        resetToken: payload.token,
      },
    },
  })

  const { customer, customerAccessToken } = res.body.data.customerReset

  return {
    customer,
    customerAccessToken,
  }
}

export async function getCustomer(customerAccessToken: string) {
  const res = await commerceFetch<ShopifyGetCustomerResponseType>({
    query: getCustomerQuery,
    variables: {
      customerAccessToken,
    },
  })

  return res.body.data.customer
}

export async function updateCustomer(
  firstName: string,
  lastName: string,
): Promise<any> {
  const customerAccessToken = Cookie.get(USER_ACCESS_TOKEN)

  const res = await commerceFetch<any>({
    query: updateCustomerMutation,
    variables: {
      customerAccessToken,
      customer: {
        firstName,
        lastName,
      },
    },
  })

  const customer = res.body.data.customerUpdate.customer

  return customer
}

export async function logoutCustomer(customerAccessToken: string) {
  const res = await commerceFetch<ShopifyGetCustomerResponseType>({
    query: logoutCustomerMutation,
    variables: {
      customerAccessToken,
    },
  })

  return res.body.data
}
